import React, { useState, useEffect } from 'react';
import { Heart, Phone, Clock, Shield, CheckCircle, AlertCircle, Star } from 'lucide-react';
import { Helmet } from 'react-helmet';

import Header from '../../components/ui/Header';
import { getDynamicPhoneNumber } from '../../services/constants';

const SpanishLanguageLandingPage = () => {
  const [fallbackPhone, setFallbackPhone] = useState('+19168330052');

  // Set up dynamic fallback phone number - Spanish-optimized
  useEffect(() => {
    const updateFallbackPhone = async () => {
      try {
        const dynamicPhone = await getDynamicPhoneNumber();
        setFallbackPhone(dynamicPhone);
      } catch (error) {
        setFallbackPhone('+19168330052');
      }
    };

    updateFallbackPhone();

    const handleCurrentAgentChange = () => {
      updateFallbackPhone();
    };

    window.addEventListener('currentAgentChanged', handleCurrentAgentChange);

    return () => {
      window.removeEventListener('currentAgentChanged', handleCurrentAgentChange);
    };
  }, []);

  const formatPhoneDisplay = (phone) => {
    return phone?.replace('+1', '')?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  // Spanish-focused structured data for reviews
  const reviewsStructuredData = {
    "@context": "https://schema.org",
    "@type": "Review",
    "itemReviewed": {
      "@type": "LocalBusiness",
      "name": "Sailors Bail Bonds - Servicios en Español"
    },
    "reviewRating": {
      "@type": "Rating", 
      "ratingValue": "5",
      "bestRating": "5"
    },
    "author": {
      "@type": "Person",
      "name": "Carmen M."
    },
    "reviewBody": "Hablaron español conmigo desde el primer momento. Me explicaron todo claramente y tuvieron a mi hijo en casa en 2 horas. Servicio profesional con comprensión cultural."
  };

  return (
    <>
      <Helmet>
        {/* Spanish SEO optimization */}
        <title>Fianza Rápida Sacramento - Sailors Bail Bonds | Servicio 24/7 en Español</title>
        <meta name="description" content="¡Traemos a su ser querido a casa de manera segura esta noche! Servicios de fianza 24/7 con agentes que hablan español. Liberación en 2-4 horas. Planes de pago desde $50. Sacramento, Stockton, Modesto. ¡Llame ahora!" />
        
        {/* Spanish keywords for AEO */}
        <meta name="keywords" content="fianza sacramento, bail bonds español, liberación rápida cárcel, agente fianza stockton, modesto bail español, fianza barata, planes de pago, servicio 24/7 español, ayuda familia crisis, servicios fianza valle central, bondsman español" />
        
        {/* Spanish geo targeting */}
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Sacramento, Stockton, Modesto" />
        <meta name="ICBM" content="38.5767, -121.4934" />
        
        {/* Spanish language tags */}
        <meta name="language" content="es-US" />
        <meta http-equiv="content-language" content="es-US" />
        
        {/* Rich snippets for Spanish business */}
        <script type="application/ld+json">
          {JSON.stringify(reviewsStructuredData)}
        </script>
        
        {/* Hreflang for Spanish targeting */}
        <link rel="alternate" href="https://sailorsbailbonds.com/spanish-language-landing-page" hreflang="es-US" />
        <link rel="alternate" href="https://sailorsbailbonds.com/" hreflang="en-US" />
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Crisis Support Banner - Spanish */}
        <section className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white py-3 px-4 shadow-lg sticky top-0 z-50" role="banner" aria-label="Contacto de crisis en español">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-center text-center space-y-2 sm:space-y-0 sm:space-x-6">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 animate-pulse" aria-hidden="true" />
                <span className="font-bold">Llame Ahora (916) 833-0052</span>
              </div>
              <div className="text-sm opacity-90">Mensaje de Texto | Español</div>
              <a 
                href={`tel:${fallbackPhone}`}
                className="bg-white text-red-600 px-6 py-2 rounded-full font-bold hover:bg-red-50 transition-colors text-sm"
                aria-label={`Llamar servicios de fianza en español ${formatPhoneDisplay(fallbackPhone)}`}
              >
                Llamar: {formatPhoneDisplay(fallbackPhone)}
              </a>
            </div>
          </div>
        </section>

        {/* Hero Section - Spanish Crisis Messaging */}
        <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white overflow-hidden" role="main">
          {/* Background Images - Family Reunion Pictures */}
          <div className="absolute inset-0" aria-hidden="true">
            {/* Family Hug Image Background */}
            <div 
              className="absolute top-0 right-0 w-1/3 h-full opacity-15 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1609220136736-443140cffec6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80')`,
                backgroundBlendMode: 'overlay'
              }}
              alt="Reunión familiar emocional después de liberación de la cárcel"
            ></div>
            
            {/* Hispanic Family Portrait */}
            <div 
              className="absolute bottom-0 left-0 w-1/2 h-2/3 opacity-10 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1566430363739-b2986e628ffe?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80')`,
                backgroundBlendMode: 'overlay'
              }}
              alt="Retrato de familia hispana unida celebrando juntos"
            ></div>
          </div>

          {/* Background pattern overlay */}
          <div className="absolute inset-0 opacity-10 z-10" aria-hidden="true">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px)',
              backgroundSize: '50px 50px'
            }}></div>
          </div>

          <div className="relative z-20 max-w-7xl mx-auto px-6 py-20 lg:py-28">
            <div className="text-center max-w-5xl mx-auto">
              {/* Trust Badge - Spanish */}
              <div className="inline-flex items-center bg-green-500/20 border border-green-400 text-green-100 px-6 py-2 rounded-full mb-8 backdrop-blur-sm" role="note">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3 animate-pulse" aria-hidden="true"></div>
                Agentes Licenciados Disponibles - Hablamos Español
              </div>

              <h1 className="text-4xl lg:text-7xl font-black mb-8 leading-tight">
                <span className="block bg-gradient-to-r from-yellow-300 to-amber-300 bg-clip-text text-transparent">
                  Fianza Rápida Sacramento
                </span>
                <span className="block text-white text-3xl lg:text-5xl mt-4">
                  Traiga a Su Ser Querido a Casa de Manera Segura
                </span>
              </h1>
              
              <h2 className="text-xl lg:text-2xl mb-6 text-blue-100 leading-relaxed font-semibold">
                Liberación Rápida de la Cárcel en Sacramento, Stockton, Modesto - Promedio 2-4 Horas
              </h2>
              
              <div className="text-lg lg:text-xl mb-12 text-blue-200 leading-relaxed max-w-4xl mx-auto">
                <strong className="text-white">¿Arrestaron a su ser querido?</strong> Nuestros agentes licenciados que hablan español brindan 
                <strong className="text-white"> servicio profesional y compasivo con planes de pago desde solo $50.</strong>
                Estamos aquí 24/7 para guiarle a través del proceso y traer a su familiar a casa de manera segura.
              </div>

              {/* Primary Actions - Spanish */}
              <div className="flex flex-col lg:flex-row gap-4 justify-center items-center mb-16">
                <a 
                  href={`tel:${fallbackPhone}`}
                  className="group bg-gradient-to-r from-yellow-400 to-amber-500 text-black px-8 lg:px-10 py-4 lg:py-5 rounded-2xl font-bold text-lg lg:text-xl hover:from-yellow-300 hover:to-amber-400 transition-all transform hover:scale-105 shadow-2xl flex items-center w-full lg:w-auto justify-center"
                  aria-label="Llamar servicio de fianza para ayuda inmediata en español"
                >
                  <Phone className="w-6 h-6 lg:w-7 lg:h-7 mr-3 group-hover:animate-pulse" aria-hidden="true" />
                  Llamar 24/7
                </a>
                
                <div className="text-blue-200 text-sm font-medium">Disponible 24/7/365 - Nunca Cerrado</div>
                
                <a 
                  href={`tel:${fallbackPhone}`}
                  className="group bg-green-600 hover:bg-green-500 text-white px-6 lg:px-8 py-4 lg:py-5 rounded-2xl font-bold text-base lg:text-lg transition-all transform hover:scale-105 shadow-2xl border-2 border-green-500 flex items-center whitespace-nowrap min-w-max w-full lg:w-auto justify-center"
                  aria-label={`Línea directa del agente en español ${formatPhoneDisplay(fallbackPhone)}`}
                >
                  <Phone className="w-5 h-5 lg:w-6 lg:h-6 mr-2 group-hover:animate-pulse flex-shrink-0" aria-hidden="true" />
                  <span className="whitespace-nowrap">Agente en Servicio: {formatPhoneDisplay(fallbackPhone)}</span>
                </a>
              </div>

              {/* Trust Indicators - Spanish */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" role="list" aria-label="Garantías de servicio en español">
                <div className="flex flex-col items-center p-6 bg-white/10 rounded-2xl backdrop-blur-sm border border-white/20" role="listitem">
                  <Clock className="w-12 h-12 text-yellow-300 mb-4" aria-hidden="true" />
                  <h3 className="font-bold text-lg mb-2">Abierto 24/7</h3>
                  <p className="text-blue-200">Agentes disponibles toda hora, todos los días</p>
                </div>
                <div className="flex flex-col items-center p-6 bg-white/10 rounded-2xl backdrop-blur-sm border border-white/20" role="listitem">
                  <Shield className="w-12 h-12 text-yellow-300 mb-4" aria-hidden="true" />
                  <h3 className="font-bold text-lg mb-2">0% Inicial OAC</h3>
                  <p className="text-blue-200">Pago por teléfono - Venimos a usted</p>
                </div>
                <div className="flex flex-col items-center p-6 bg-white/10 rounded-2xl backdrop-blur-sm border border-white/20" role="listitem">
                  <Heart className="w-12 h-12 text-yellow-300 mb-4" aria-hidden="true" />
                  <h3 className="font-bold text-lg mb-2">Hablamos Español</h3>
                  <p className="text-blue-200">Agentes bilingües con comprensión cultural</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 60-Second Form Section - Spanish */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden" role="region" aria-labelledby="form-heading">
          <div className="relative z-10 max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 id="form-heading" className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">
                Comience la Fianza en Línea - 60 Segundos
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Complete este formulario rápido y recibirá un mensaje de texto de inmediato con el siguiente paso.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200">
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-bold text-gray-900 mb-2">
                      Su Nombre Completo *
                    </label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      className="w-full p-4 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-0 text-lg"
                      placeholder="Ej: María González"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="telefono" className="block text-sm font-bold text-gray-900 mb-2">
                      Su Número de Teléfono *
                    </label>
                    <input
                      type="tel"
                      id="telefono"
                      name="telefono"
                      className="w-full p-4 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-0 text-lg"
                      placeholder="(916) 555-1234"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="detenido" className="block text-sm font-bold text-gray-900 mb-2">
                      Nombre de la Persona Detenida *
                    </label>
                    <input
                      type="text"
                      id="detenido"
                      name="detenido"
                      className="w-full p-4 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-0 text-lg"
                      placeholder="Nombre completo del detenido"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="carcel" className="block text-sm font-bold text-gray-900 mb-2">
                      Cárcel (si la conoce)
                    </label>
                    <select
                      id="carcel"
                      name="carcel"
                      className="w-full p-4 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-0 text-lg"
                    >
                      <option value="">Seleccionar cárcel...</option>
                      <option value="sacramento-county">Cárcel del Condado de Sacramento</option>
                      <option value="san-joaquin-county">Cárcel del Condado de San Joaquín</option>
                      <option value="stanislaus-county">Cárcel del Condado de Stanislaus</option>
                      <option value="placer-county">Cárcel del Condado de Placer</option>
                      <option value="el-dorado-county">Cárcel del Condado de El Dorado</option>
                      <option value="no-se">No estoy seguro/a</option>
                    </select>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" aria-hidden="true" />
                    Qué Pasa Después de Enviar:
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <span className="font-bold text-blue-600 mr-2">1.</span>
                      <span>Recibirá un mensaje de texto de confirmación inmediatamente</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold text-blue-600 mr-2">2.</span>
                      <span>Un agente que habla español lo llamará en menos de 10 minutos</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold text-blue-600 mr-2">3.</span>
                      <span>Le enviaremos un enlace seguro para completar el papeleo</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold text-blue-600 mr-2">4.</span>
                      <span>Comenzamos el proceso de liberación inmediatamente</span>
                    </li>
                  </ul>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-5 rounded-2xl font-black text-xl hover:from-red-700 hover:to-red-800 transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center"
                >
                  <span className="mr-3">📱</span>
                  Enviar - Recibir Mensaje de Texto Ahora
                </button>

                <div className="text-center text-sm text-gray-600">
                  <p>
                    <strong>Privacidad garantizada:</strong> Su información está segura y nunca se comparte.
                    <br />
                    <strong>Sin obligación:</strong> Si no procede, no paga nada.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* Social Proof Section - Spanish */}
        <section className="py-20 bg-white relative overflow-hidden" role="region" aria-labelledby="proof-heading">
          <div className="relative z-10 max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 id="proof-heading" className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">
                Prueba Social y Calificaciones
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Familias reales que hemos ayudado en la comunidad hispana de Sacramento.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-16" role="list" aria-label="Testimonios de clientes hispanos">
              {[
                {
                  quote: "Hablaron español conmigo desde el primer momento. Me explicaron todo claramente y tuvieron a mi hijo en casa en 2 horas. Servicio profesional con comprensión cultural.",
                  name: "Carmen M.",
                  role: "Madre de Sacramento",
                  rating: 5,
                  location: "Condado de Sacramento"
                },
                {
                  quote: "Llamé a las 3 AM llorando - no sabía qué hacer. El agente fue muy paciente, me explicó todo en español, y mi esposo estaba en casa antes del desayuno.",
                  name: "Roberto L.", 
                  role: "Esposo de Stockton",
                  rating: 5,
                  location: "Condado de San Joaquín"
                },
                {
                  quote: "No tenía dinero, no tenía crédito. Trabajaron conmigo en un plan de pagos por solo $50 inicial y liberaron a mi hermana el mismo día. Nos trataron como familia.",
                  name: "Ana R.",
                  role: "Hermana de Modesto", 
                  rating: 5,
                  location: "Condado de Stanislaus"
                }
              ]?.map((testimonial, index) => (
                <article key={index} className="bg-gray-50 p-8 rounded-2xl shadow-lg border-l-4 border-blue-500" role="listitem">
                  <div className="flex mb-4" role="img" aria-label={`Reseña de ${testimonial?.rating} estrellas`}>
                    {[...Array(testimonial?.rating)]?.map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" aria-hidden="true" />
                    ))}
                  </div>
                  <blockquote className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                    "{testimonial?.quote}"
                  </blockquote>
                  <footer className="border-t border-gray-200 pt-4">
                    <div className="font-bold text-gray-900">{testimonial?.name}</div>
                    <div className="text-gray-600">{testimonial?.role}</div>
                    <div className="text-sm text-blue-600 font-medium">{testimonial?.location}</div>
                  </footer>
                </article>
              ))}
            </div>

            {/* BBB and Rating Badges */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white p-12 rounded-2xl text-center">
              <h3 className="text-2xl font-bold mb-8">Calificaciones y Acreditaciones</h3>
              <div className="grid md:grid-cols-2 gap-8" role="list" aria-label="Calificaciones y acreditaciones">
                <div className="flex flex-col items-center" role="listitem">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4">
                    <span className="text-2xl">⭐⭐⭐⭐⭐</span>
                  </div>
                  <h4 className="font-bold mb-2">Google Reviews</h4>
                  <p className="text-blue-200 text-sm">4.8/5 estrellas - 200+ reseñas</p>
                </div>
                <div className="flex flex-col items-center" role="listitem">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4">
                    <Shield className="w-10 h-10 text-blue-900" aria-hidden="true" />
                  </div>
                  <h4 className="font-bold mb-2">Licencia CA</h4>
                  <p className="text-blue-200 text-sm">CA LIC# 1N13041 - Completamente asegurado</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Explanation - Spanish */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden" role="region" aria-labelledby="process-heading">
          <div className="relative z-10 max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 id="process-heading" className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">
                Lo Que Hacemos Por Usted en los Primeros 10 Minutos
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Proceso simple de 5 pasos - Lo manejamos todo mientras usted se queda tranquilo/a.
              </p>
            </div>

            <div className="grid lg:grid-cols-5 gap-8" role="list" aria-label="Proceso de fianza paso a paso">
              {[
                {
                  step: "1",
                  title: "Confirmamos los Cargos",
                  description: "Verificamos los cargos y el monto de la fianza inmediatamente",
                  icon: "📋"
                },
                {
                  step: "2", 
                  title: "Enlace Seguro",
                  description: "Le enviamos un enlace seguro para firmar electrónicamente",
                  icon: "📱"
                },
                {
                  step: "3",
                  title: "Organizamos el Pago",
                  description: "Arreglamos el pago por teléfono - sin visita a la oficina",
                  icon: "💳"
                },
                {
                  step: "4",
                  title: "Notificamos la Cárcel",
                  description: "Notificamos a la cárcel y comenzamos la liberación",
                  icon: "🏛️"
                },
                {
                  step: "5",
                  title: "Actualizaciones por Texto",
                  description: "Le enviamos actualizaciones por mensaje de texto",
                  icon: "✅"
                }
              ]?.map((step, index) => (
                <div key={index} className="bg-white p-6 rounded-2xl shadow-lg text-center border-t-4 border-blue-500" role="listitem">
                  <div className="text-4xl mb-4" aria-hidden="true">{step?.icon}</div>
                  <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4">
                    {step?.step}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-3">{step?.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step?.description}</p>
                </div>
              ))}
            </div>

            {/* Risk Reversal - Spanish */}
            <div className="mt-16 bg-green-50 p-8 rounded-2xl border border-green-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Garantías Sin Riesgo
              </h3>
              <div className="grid md:grid-cols-3 gap-6 text-center" role="list">
                <div className="flex flex-col items-center" role="listitem">
                  <CheckCircle className="w-8 h-8 text-green-600 mb-3" aria-hidden="true" />
                  <h4 className="font-bold mb-2">Consulta Sin Obligación</h4>
                  <p className="text-gray-600 text-sm">Si no procede, no paga nada</p>
                </div>
                <div className="flex flex-col items-center" role="listitem">
                  <CheckCircle className="w-8 h-8 text-green-600 mb-3" aria-hidden="true" />
                  <h4 className="font-bold mb-2">Precios Transparentes</h4>
                  <p className="text-gray-600 text-sm">Sin tarifas ocultas o sorpresas</p>
                </div>
                <div className="flex flex-col items-center" role="listitem">
                  <CheckCircle className="w-8 h-8 text-green-600 mb-3" aria-hidden="true" />
                  <h4 className="font-bold mb-2">Igualamos Tarifas</h4>
                  <p className="text-gray-600 text-sm">Igualamos cualquier tarifa escrita de agencia licenciada</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas - Spanish */}
        <section className="py-20 bg-white relative overflow-hidden" role="region" aria-labelledby="areas-heading">
          <div className="relative z-10 max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 id="areas-heading" className="text-3xl lg:text-4xl font-black text-gray-900 mb-6">
                Servimos Todo el Valle Central y Norte de California
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Agentes licenciados que hablan español disponibles en todas las principales cárceles y condados.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12" role="list" aria-label="Cobertura de áreas de servicio">
              {[
                {
                  region: "Valle Central",
                  counties: [
                    {
                      name: "Condado de San Joaquín",
                      facilities: ["Cárcel del Condado de San Joaquín", "Cárcel de la Ciudad de Stockton", "Instalación French Camp"],
                      responseTime: "45 minutos promedio"
                    },
                    {
                      name: "Condado de Stanislaus", 
                      facilities: ["Cárcel del Condado de Stanislaus", "Cárcel de la Ciudad de Modesto", "Centro de Seguridad Pública"],
                      responseTime: "1 hora promedio"
                    }
                  ]
                },
                {
                  region: "Norte de California",
                  counties: [
                    {
                      name: "Condado de Sacramento",
                      facilities: ["Cárcel Principal del Condado de Sacramento", "Centro Correccional Rio Cosumnes", "Cárcel de la Ciudad de Sacramento"],
                      responseTime: "30 minutos promedio"
                    },
                    {
                      name: "Condado de Placer",
                      facilities: ["Cárcel del Condado de Placer", "Centro de Justicia Auburn"],
                      responseTime: "1 hora promedio"
                    }
                  ]
                }
              ]?.map((region, regionIndex) => (
                <div key={regionIndex} className="space-y-8" role="listitem">
                  <div className="text-center">
                    <h3 className="text-2xl font-black text-gray-900 mb-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl">
                      {region?.region}
                    </h3>
                  </div>
                  
                  <div className="space-y-6">
                    {region?.counties?.map((county, countyIndex) => (
                      <article key={countyIndex} className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
                        <h4 className="text-xl font-bold text-gray-900 mb-3">
                          {county?.name}
                        </h4>
                        
                        <div className="text-gray-600 text-sm mb-4">
                          <div className="font-semibold mb-2">Instalaciones Principales:</div>
                          <ul className="space-y-1">
                            {county?.facilities?.map((facility, facilityIdx) => (
                              <li key={facilityIdx}>{facility}</li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="inline-flex items-center bg-green-100 text-green-800 px-3 py-2 rounded-full font-semibold text-sm">
                          <CheckCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                          {county?.responseTime}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA - Spanish Crisis Messaging */}
        <section className="py-20 bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white relative overflow-hidden" role="region" aria-labelledby="final-cta-heading">
          {/* Background Images */}
          <div className="absolute inset-0" aria-hidden="true">
            {/* Family Reunion Joy */}
            <div 
              className="absolute inset-0 opacity-10 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1609220136736-443140cffec6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80')`,
                backgroundBlendMode: 'overlay'
              }}
              alt="Celebración de reunión familiar después del proceso exitoso de fianza"
            ></div>
          </div>

          <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
            <div className="mb-8">
              <AlertCircle className="w-16 h-16 mx-auto mb-6 animate-pulse" aria-hidden="true" />
              <h2 id="final-cta-heading" className="text-4xl lg:text-6xl font-black mb-6">
                ¿Necesita Fianza en Sacramento Ahora Mismo?
              </h2>
              <p className="text-2xl lg:text-3xl mb-4 leading-tight">
                Respondemos en menos de 10 segundos, día y noche
              </p>
              <p className="text-xl lg:text-2xl text-red-100 mb-12">
                Comience llamando al (916) 833-0052 o toque "Comenzar Fianza en Línea" - le enviaremos un enlace seguro y manejaremos el resto.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 justify-center items-center">
              <a 
                href={`tel:${fallbackPhone}`}
                className="group bg-white text-red-600 px-8 lg:px-12 py-5 lg:py-6 rounded-2xl font-black text-xl lg:text-2xl hover:bg-red-50 transition-all transform hover:scale-105 shadow-2xl flex items-center w-full lg:w-auto justify-center"
                aria-label="Llamar servicios de fianza inmediatamente en español"
              >
                <Phone className="w-8 h-8 mr-4 group-hover:animate-bounce" aria-hidden="true" />
                Llamar 24/7: {formatPhoneDisplay(fallbackPhone)}
              </a>
              
              <div className="text-red-100 font-bold">Agentes que Hablan Español Disponibles Ahora</div>
            </div>

            <div className="mt-12 bg-white/20 rounded-2xl p-8 backdrop-blur-sm" role="complementary">
              <div className="grid md:grid-cols-2 gap-6 text-lg" role="list" aria-label="Garantías de servicio">
                <div className="flex items-center" role="listitem">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-300" aria-hidden="true" />
                  <span><strong>Disponible 24/7/365</strong> - Nunca cerrado, siempre aquí</span>
                </div>
                <div className="flex items-center" role="listitem">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-300" aria-hidden="true" />
                  <span><strong>Planes de Pago</strong> - Trabajamos con cualquier presupuesto</span>
                </div>
                <div className="flex items-center" role="listitem">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-300" aria-hidden="true" />
                  <span><strong>Licenciados y Asegurados</strong> - Servicio completamente asegurado</span>
                </div>
                <div className="flex items-center" role="listitem">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-300" aria-hidden="true" />
                  <span><strong>Servimos Todos los Condados</strong> - Viajamos a cualquier lugar</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Language Toggle Footer */}
        <section className="py-8 bg-gray-900 text-white text-center" role="contentinfo">
          <div className="max-w-4xl mx-auto px-6">
            <h3 className="text-lg font-bold mb-4">Cambiar de Idioma / Language Toggle</h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="/"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors flex items-center"
              >
                🇺🇸 English Version
              </a>
              <span className="text-gray-400">|</span>
              <span className="bg-red-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center">
                🇲🇽 Versión en Español (Actual)
              </span>
            </div>
            <div className="mt-6 text-sm text-gray-400">
              <p>
                Sailors Bail Bonds - CA LIC# 1N13041 | 901 H Street, Suite 103, Sacramento, CA 95814
                <br />
                Servicio profesional de fianzas disponible en inglés y español las 24 horas
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SpanishLanguageLandingPage;